import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { del, post } from "common/helpers/HTTP";
import { getProxyConfigPayload } from "common/helpers/utils";
import useAppStore from "common/hooks/useAppStore";
import type { WorkspaceAccount } from "common/types";

import useWorkspaceCacheUpdate from "./useWorkspaceCacheUpdate";

async function loginRequest(
  workspaceId: string,
  loginData: { email: string; password: string; selectedProxy: string },
): Promise<
  | {
      loginResult: "PASS";
      profile: WorkspaceAccount;
    }
  | {
      loginResult: "CHALLENGE";
      challengeType:
        | "EmailPinChallenge"
        | "SMSPinChallenge"
        | "AuthenticatorAppChallenge"
        | "CaptchaChallenge"
        | "AppChallenge";
    }
  | {
      loginResult: "BAD_PASSWORD";
    }
> {
  const { email, password, selectedProxy } = loginData;

  const {
    login_result: loginResult,
    challenge_type: challengeType,
    profile,
  } = await post(`workspaces/${workspaceId}/accounts/login`, {
    email,
    password,
    proxy_config: getProxyConfigPayload(selectedProxy),
    debug: useAppStore.getState().debugOptions.shouldDebugLogin,
  });

  return { loginResult, challengeType, profile };
}
async function resolveChallengeRequest(
  workspaceId: string,
  email: string,
  pin: string,
): Promise<{
  loginResult: "PASS";
  profile: WorkspaceAccount;
}> {
  const { login_result: loginResult, profile } = await post(
    `workspaces/${workspaceId}/accounts/challenge/${email}/resolve`,
    { pin },
  );
  return { loginResult, profile };
}
async function resendChallengeRequest(workspaceId: string, email: string) {
  await post(`workspaces/${workspaceId}/accounts/challenge/${email}/resend`);
}
async function deleteAccountRequest(workspaceId: string, accountId: string) {
  await del(`workspaces/${workspaceId}/accounts/${accountId}`);
}

export default function useWorkspaceAccountMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const {
    updateCachedWorkspaceDetails,
    updateCachedWorkspaceList,
    updateCachedWorkspaceAccounts,
  } = useWorkspaceCacheUpdate();

  const { mutateAsync: login, isPending: isLoggingIn } = useMutation({
    mutationFn: ({
      loginData,
    }: {
      loginData: { email: string; password: string; selectedProxy: string };
    }) => loginRequest(workspaceId, loginData),
    onSuccess: (response) => {
      if (response.loginResult === "PASS") {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          // Check if the account has already been connected (Reconnect case)
          const existingAccount = draftAccounts.find(
            ({ id }) => id === response.profile.id,
          );

          if (existingAccount) {
            existingAccount.state = "LoggedIn";
          } else {
            draftAccounts.push(response.profile);
          }
        });

        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          draftWorkspace.accounts_count += 1;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.accounts_count += 1;
        });

        toast.success(`Account added - ${response.profile.full_name}`);
      }
    },
  });
  const { mutateAsync: deleteAccount, isPending: isDeletingAccount } =
    useMutation({
      mutationFn: ({ accountId }: { accountId: string }) =>
        deleteAccountRequest(workspaceId, accountId),
      onSuccess: (_, { accountId }) => {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          return draftAccounts.filter(({ id }) => id !== accountId);
        });

        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          draftWorkspace.accounts_count -= 1;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.accounts_count -= 1;
        });
      },
    });
  const { mutateAsync: resolveChallenge, isPending: isResolvingChallenge } =
    useMutation({
      mutationFn: ({ email, pin }: { email: string; pin: string }) =>
        resolveChallengeRequest(workspaceId, email, pin),
      onSuccess: ({ loginResult, profile }) => {
        if (loginResult === "PASS") {
          updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
            // Check if the account has already been connected (Reconnect case)
            const existingAccount = draftAccounts.find(
              ({ id }) => id === profile.id,
            );

            if (existingAccount) {
              existingAccount.state = "LoggedIn";
            } else {
              draftAccounts.push(profile);
            }
          });

          updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
            draftWorkspace.accounts_count += 1;
          });
          updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
            draftWorkspace.accounts_count += 1;
          });

          toast.success(`Account added - ${profile.full_name}`);
        }
      },
    });

  const { mutateAsync: resendChallenge, isPending: isResendingChallenge } =
    useMutation({
      mutationFn: ({ email }: { email: string }) =>
        resendChallengeRequest(workspaceId, email),
    });

  return {
    login,
    isLoggingIn,
    deleteAccount,
    isDeletingAccount,
    resolveChallenge,
    isResolvingChallenge,
    resendChallenge,
    isResendingChallenge,
  };
}
